import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from './api';
import type { AcademicYearWriteInput, CreateSchoolInput, UpdateSchoolInput } from './types';

/** Query-key factory — every schools key derives from here. */
export const schoolKeys = {
  all: ['schools'] as const,
  mine: () => [...schoolKeys.all, 'mine'] as const,
};

export function useMySchools() {
  return useQuery({
    queryKey: schoolKeys.mine(),
    queryFn: () => api.getMySchools(),
  });
}

export function useCreateSchool() {
  return useMutation({
    mutationFn: (input: CreateSchoolInput) => api.createSchool(input),
    // No invalidation here: the caller refreshes the whole session (memberships
    // live in the session store, and the active school changes).
  });
}

export function useUpdateSchool() {
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: UpdateSchoolInput }) =>
      api.updateSchool(publicId, input),
    // Same reason as useCreateSchool: the active school is session state, not a
    // query — the caller refreshes the session to adopt the server's read-back.
  });
}

// ---------------------------------------------------------------------------
// Academic years (backend ADR-031). Class payloads embed the year, so every
// year mutation also invalidates the classes cache — cheap, and it keeps the
// year names on class rows honest without cross-feature imports.
// ---------------------------------------------------------------------------

export const academicYearKeys = {
  all: ['academic-years'] as const,
  list: () => [...academicYearKeys.all, 'list'] as const,
};

const CLASSES_CACHE_ROOT = ['classes'] as const;

export function useAcademicYears(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: academicYearKeys.list(),
    queryFn: () => api.getAcademicYears(),
    enabled: options.enabled,
  });
}

/** The current year out of the (cached) list — `undefined` while loading. */
export function useCurrentAcademicYear() {
  const years = useAcademicYears();
  return years.data?.results.find(year => year.is_current);
}

export function useCreateAcademicYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AcademicYearWriteInput) => api.createAcademicYear(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: academicYearKeys.all }),
  });
}

export function useUpdateAcademicYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      input,
    }: {
      publicId: string;
      input: Partial<AcademicYearWriteInput>;
    }) => api.updateAcademicYear(publicId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: academicYearKeys.all });
      void qc.invalidateQueries({ queryKey: CLASSES_CACHE_ROOT });
    },
  });
}

export function useSetCurrentAcademicYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.setCurrentAcademicYear(publicId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: academicYearKeys.all });
      void qc.invalidateQueries({ queryKey: CLASSES_CACHE_ROOT });
    },
  });
}

export function useDeleteAcademicYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteAcademicYear(publicId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: academicYearKeys.all }),
  });
}
